import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { authService } from '@/services';
import { useI18n } from '@/i18n/use-i18n';
import '@/styles/auth-page.css';

type LoginFormValues = {
  email: string;
  password: string;
};

export function Login() {
  const { strings } = useI18n();
  const navigate = useNavigate();
  const [generalError, setGeneralError] = useState('');
  const highlights = strings.login.highlights;
  const loginSchema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .required(strings.login.validations.emailRequired)
          .email(strings.login.validations.emailInvalid),
        password: yup
          .string()
          .required(strings.login.validations.passwordRequired)
          .min(8, strings.login.validations.passwordMin),
      }),
    [strings],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const isFormDisabled = isSubmitting;

  const onSubmit = async (values: LoginFormValues) => {
    setGeneralError('');
    const { error } = await authService.login({
      email: values.email,
      password: values.password,
    });

    if (error) {
      setGeneralError(error.message);
      return;
    }

    navigate('/dashboard', { replace: true });
  };

  return (
    <main className="auth-page">
      <section className="auth-page__hero">
        <div className="auth-page__hero-content">
          <p className="auth-page__badge">Touzi</p>
          <h1>
            {strings.login.heroTitle} <span>{strings.login.heroEmphasis}</span>
          </h1>
          <p className="auth-page__subtitle">
            {strings.login.subtitle}
          </p>
          <ul className="auth-page__highlights">
            {highlights.map((item) => (
              <li key={item.title}>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="auth-page__panel" aria-label={strings.login.panelAriaLabel}>
        <div className="auth-page__card">
          <header>
            <h2>{strings.login.formTitle}</h2>
            <p>{strings.login.formSubtitle}</p>
          </header>

          <form className="auth-page__form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <label htmlFor="email">{strings.login.emailLabel}</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={strings.login.emailPlaceholder}
              {...register('email')}
              aria-invalid={errors.email ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.email && (
              <span className="auth-page__field-error" role="alert">
                {errors.email.message}
              </span>
            )}

            <label htmlFor="password">{strings.login.passwordLabel}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              placeholder={strings.login.passwordPlaceholder}
              {...register('password')}
              aria-invalid={errors.password ? 'true' : 'false'}
              disabled={isFormDisabled}
            />
            {errors.password && (
              <span className="auth-page__field-error" role="alert">
                {errors.password.message}
              </span>
            )}

            {generalError && (
              <div
                className="auth-page__general-feedback auth-page__general-feedback--error"
                role="alert"
                aria-live="assertive"
              >
                {generalError}
              </div>
            )}

            <button type="submit" className="auth-page__submit" disabled={isFormDisabled}>
              {isSubmitting ? strings.login.submitLoading : strings.login.submitLabel}
            </button>
          </form>

          <p className="auth-page__switch">
            {strings.login.switchPrompt} <Link to="/signup">{strings.login.switchAction}</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
